import React, { useMemo, useState, useEffect } from 'react';
import {
  Plus,
  FileText,
  Database,
  RefreshCw,
  Pencil,
  Trash2,
  X,
  Search,
} from 'lucide-react';
import AddEditScheduleModal, { ScheduleFormValues } from './AddEditScheduleModal';
import CapturedDataModal from './CapturedDataModal';
import ConfirmPopup from './ConfirmPopup';
import { ingestionService } from '../../../../components/api/ingestionService';

// --- Types ---
type ScheduleStatus = 'active' | 'running' | 'idle' | 'error';

interface Schedule {
  id: string;
  name: string;
  entity: string;
  destination: string;
  strategy: string;
  schedule: string;
  lastSynced: string;
  lastSyncedMeta?: string; // e.g. "1,247 rows" or "Running... ~42s" or "Failed 1hr ago"
  status: ScheduleStatus;
  enabled: boolean;
  icon?: string;
  strategyOptions?: { watermarkColumn?: string; primaryKey?: string };
  /** From API: for edit form and payload */
  entityId?: string;
  destinationId?: string;
  destinationType?: string;
  frequency?: string;
  runAt?: string;
  entityName?: string;
  destinationName?: string;
}

/** Map API schedule response to Schedule (list item) */
function mapApiScheduleToSchedule(item: {
  id: string;
  name: string;
  entityId: string;
  entityName?: string | null;
  destinationType: string;
  destinationId: string;
  destinationName?: string | null;
  strategy: string;
  watermarkColumn?: string | null;
  primaryKey?: string | null;
  schedule: string;
  frequency?: string;
  runAt?: string;
  enabled: boolean;
}): Schedule {
  return {
    id: item.id,
    name: item.name,
    entity: item.entityName || item.entityId,
    destination: item.destinationName || item.destinationType,
    strategy: item.strategy,
    schedule: item.schedule,
    lastSynced: 'Never',
    status: item.enabled ? 'active' : 'idle',
    enabled: item.enabled,
    icon: '📄',
    strategyOptions:
      item.watermarkColumn || item.primaryKey
        ? { watermarkColumn: item.watermarkColumn ?? undefined, primaryKey: item.primaryKey ?? undefined }
        : undefined,
    entityId: item.entityId,
    destinationId: item.destinationId,
    destinationType: item.destinationType,
    frequency: item.frequency,
    runAt: item.runAt,
    entityName: item.entityName ?? undefined,
    destinationName: item.destinationName ?? undefined,
  };
}

interface LogRow {
  id: string;
  time: string;
  level: 'SUCCESS' | 'INFO' | 'WARN' | 'ERROR';
  message: string;
}

interface DataRow {
  id: string;
  email: string;
  name: string;
  segment: string;
  ltv: string;
  updated_at: string;
}

// --- Mock data (used for logs/data only; schedules loaded from API) ---
const MOCK_SCHEDULES: Schedule[] = [];

const MOCK_LOGS: LogRow[] = [
  { id: 'l1', time: '02:00:31', level: 'SUCCESS', message: '1,247 records upserted to KB' },
  { id: 'l2', time: '02:00:08', level: 'INFO', message: 'Generating embeddings batch 1/13' },
  { id: 'l3', time: '02:00:05', level: 'INFO', message: 'Deduplication check: 0 duplicates found' },
  { id: 'l4', time: 'Feb 25', level: 'WARN', message: '3 rows skipped due to null primary key' },
  { id: 'l5', time: 'Feb 24', level: 'ERROR', message: 'Connection timeout after 30s, retrying' },
  { id: 'l6', time: 'Feb 24', level: 'SUCCESS', message: 'Retry successful, 890 rows synced in 44s' },
];

const MOCK_DATA_ROWS: DataRow[] = [
  { id: 'CU-10041', email: 'alice@acme.io', name: 'Alice Morgan', segment: 'enterprise', ltv: '$12,400', updated_at: '2025-02-25' },
  { id: 'CU-10042', email: 'bob@beta.co', name: 'Bob Chen', segment: 'smb', ltv: '$840', updated_at: '2025-02-25' },
  { id: 'CU-10043', email: 'carol@corp.com', name: 'Carol White', segment: 'enterprise', ltv: '$9,200', updated_at: '2025-02-25' },
  { id: 'CU-10044', email: 'dave@startup.io', name: 'Dave Park', segment: 'trial', ltv: '$0', updated_at: '2025-02-25' },
  { id: 'CU-10045', email: 'eve@mega.org', name: 'Eve Santos', segment: 'mid-market', ltv: '$3,100', updated_at: '2025-02-25' },
  { id: 'CU-10046', email: 'frank@dev.net', name: 'Frank Liu', segment: 'smb', ltv: '$620', updated_at: '2025-02-25' },
  { id: 'CU-10047', email: 'grace@fin.com', name: 'Grace Kim', segment: 'enterprise', ltv: '$28,000', updated_at: '2025-02-25' },
];

// --- Badge / Toggle helpers ---
const statusBadgeClass: Record<ScheduleStatus, string> = {
  active: 'bg-green-500/12 text-green-400',
  running: 'bg-yellow-500/12 text-yellow-400',
  idle: 'bg-zinc-500/15 text-zinc-400',
  error: 'bg-red-500/12 text-red-400',
};

const statusDotClass: Record<ScheduleStatus, string> = {
  active: 'bg-green-500 shadow-[0_0_5px_rgba(34,197,94,0.53)]',
  running: 'bg-yellow-500 animate-pulse',
  idle: 'bg-zinc-500',
  error: 'bg-red-500',
};

const logLevelBorderClass: Record<LogRow['level'], string> = {
  SUCCESS: 'border-l-2 border-l-green-500',
  INFO: 'border-l-2 border-l-neutral-500',
  WARN: 'border-l-2 border-l-yellow-500',
  ERROR: 'border-l-2 border-l-red-500',
};

const logLevelTextClass: Record<LogRow['level'], string> = {
  SUCCESS: 'text-green-400',
  INFO: 'text-zinc-400',
  WARN: 'text-yellow-400',
  ERROR: 'text-red-400',
};

// --- Toggle switch component ---
const Toggle: React.FC<{
  on: boolean;
  onChange: () => void;
  className?: string;
}> = ({ on, onChange, className = '' }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    onClick={onChange}
    className={`w-9 h-5 rounded-full relative cursor-pointer transition-colors flex-shrink-0 ${
      on ? 'bg-indigo-600' : 'bg-neutral-600'
    } ${className}`}
  >
    <span
      className={`absolute top-1 w-3.5 h-3.5 rounded-full bg-white transition-transform ${
        on ? 'left-[18px]' : 'left-1'
      }`}
    />
  </button>
);

const IngestionSchedules = () => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [schedulesLoading, setSchedulesLoading] = useState(true);
  const [schedulesError, setSchedulesError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('Add Schedule');
  const [modalSub, setModalSub] = useState('Configure a new automated data sync job');
  const [drawerLogsOpen, setDrawerLogsOpen] = useState(false);
  const [drawerDataOpen, setDrawerDataOpen] = useState(false);
  const [drawerScheduleName, setDrawerScheduleName] = useState('--');
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(null);
  const [confirmState, setConfirmState] = useState<{ type: 'toggle' | 'delete'; schedule: Schedule } | null>(null);

  const [logs, setLogs] = useState<LogRow[]>(MOCK_LOGS);
  const [logsSearchQuery, setLogsSearchQuery] = useState('');
  const [dataRows, setDataRows] = useState<DataRow[]>(MOCK_DATA_ROWS);

  useEffect(() => {
    let cancelled = false;
    setSchedulesLoading(true);
    setSchedulesError(null);
    ingestionService.scheduler
      .list()
      .then((list: unknown) => {
        if (cancelled) return;
        const items = Array.isArray(list) ? list : [];
        setSchedules(
          items.map((item: Record<string, unknown>) =>
            mapApiScheduleToSchedule({
              id: String(item.id ?? ''),
              name: String(item.name ?? ''),
              entityId: String(item.entityId ?? ''),
              entityName: item.entityName != null ? String(item.entityName) : null,
              destinationType: String(item.destinationType ?? ''),
              destinationId: String(item.destinationId ?? ''),
              destinationName: item.destinationName != null ? String(item.destinationName) : null,
              strategy: String(item.strategy ?? 'Incremental'),
              watermarkColumn: item.watermarkColumn != null ? String(item.watermarkColumn) : null,
              primaryKey: item.primaryKey != null ? String(item.primaryKey) : null,
              schedule: String(item.schedule ?? ''),
              frequency: item.frequency != null ? String(item.frequency) : undefined,
              runAt: item.runAt != null ? String(item.runAt) : undefined,
              enabled: Boolean(item.enabled),
            })
          )
        );
      })
      .catch((err: unknown) => {
        if (!cancelled) setSchedulesError(err instanceof Error ? err.message : 'Failed to load schedules');
      })
      .finally(() => {
        if (!cancelled) setSchedulesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredLogs = useMemo(() => {
    if (!logsSearchQuery.trim()) return logs;
    const q = logsSearchQuery.toLowerCase();
    return logs.filter(
      (l) =>
        l.time.toLowerCase().includes(q) ||
        l.level.toLowerCase().includes(q) ||
        l.message.toLowerCase().includes(q)
    );
  }, [logs, logsSearchQuery]);

  const filteredSchedules = useMemo(() => {
    if (!searchQuery.trim()) return schedules;
    const q = searchQuery.toLowerCase();
    return schedules.filter((s) => s.name.toLowerCase().includes(q));
  }, [schedules, searchQuery]);

  const stats = useMemo(() => {
    const total = schedules.length;
    const active = schedules.filter((s) => s.status === 'active').length;
    const running = schedules.filter((s) => s.status === 'running').length;
    const errors = schedules.filter((s) => s.status === 'error').length;
    return { total, active, running, errors };
  }, [schedules]);

  const toggleScheduleEnabled = (id: string) => {
    const schedule = schedules.find((s) => s.id === id);
    const nextEnabled = schedule ? !schedule.enabled : true;
    ingestionService.scheduler
      .setEnabled(id, nextEnabled)
      .then(() =>
        setSchedules((prev) =>
          prev.map((s) =>
            s.id === id ? { ...s, enabled: nextEnabled, status: nextEnabled ? 'active' : 'idle' } : s
          )
        )
      )
      .catch((err: unknown) => {
        console.error('Toggle schedule failed:', err);
        alert(err instanceof Error ? err.message : 'Failed to update schedule');
      });
  };

  const openScheduleModal = (scheduleId?: string) => {
    setEditingScheduleId(scheduleId ?? null);
    setOverlayOpen(true);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setOverlayOpen(false);
    setEditingScheduleId(null);
  };

  const refreshSchedules = () => {
    return ingestionService.scheduler.list().then((list: unknown) => {
      const raw = list != null && typeof list === 'object' && 'data' in list ? (list as { data: unknown }).data : list;
      const items = Array.isArray(raw) ? raw : [];
      setSchedules(
        items.map((item: Record<string, unknown>) =>
          mapApiScheduleToSchedule({
            id: String(item.id ?? ''),
            name: String(item.name ?? ''),
            entityId: String(item.entityId ?? ''),
            entityName: item.entityName != null ? String(item.entityName) : null,
            destinationType: String(item.destinationType ?? ''),
            destinationId: String(item.destinationId ?? ''),
            destinationName: item.destinationName != null ? String(item.destinationName) : null,
            strategy: String(item.strategy ?? 'Incremental'),
            watermarkColumn: item.watermarkColumn != null ? String(item.watermarkColumn) : null,
            primaryKey: item.primaryKey != null ? String(item.primaryKey) : null,
            schedule: String(item.schedule ?? ''),
            frequency: item.frequency != null ? String(item.frequency) : undefined,
            runAt: item.runAt != null ? String(item.runAt) : undefined,
            enabled: Boolean(item.enabled),
          })
        )
      );
    });
  };

  const handleScheduleSubmit = (data: ScheduleFormValues) => {
    const payload = {
      name: data.name,
      entityId: data.entity,
      entityName: data.entityName ?? undefined,
      destinationType: data.destination,
      destinationId: data.destinationId ?? '',
      destinationName: data.destinationName ?? undefined,
      strategy: data.strategy,
      strategyOptions:
        data.strategy === 'Incremental' && data.strategyOptions
          ? { watermarkColumn: data.strategyOptions.watermarkColumn, primaryKey: data.strategyOptions.primaryKey }
          : undefined,
      frequency: data.frequency ?? 'Daily',
      runAt: data.runAt ?? '02:00',
      enabled: data.enabled,
    };
    const promise = data.id
      ? ingestionService.scheduler.update(data.id, payload)
      : ingestionService.scheduler.create(payload);
    promise
      .then(() => refreshSchedules())
      .then(() => closeModal())
      .catch((err: unknown) => {
        console.error('Schedule save failed:', err);
        alert(err instanceof Error ? err.message : 'Failed to save schedule');
      });
  };

  const openDrawerLogs = (name: string) => {
    setDrawerScheduleName(name);
    setDrawerLogsOpen(true);
    setOverlayOpen(true);
  };

  const openCapturedDataModal = (name: string) => {
    setDrawerScheduleName(name);
    setDrawerDataOpen(true);
    setOverlayOpen(true);
  };

  const closeDrawer = () => {
    setDrawerLogsOpen(false);
    setDrawerDataOpen(false);
    setOverlayOpen(false);
    setLogsSearchQuery('');
  };

  const handleOverlayClick = () => {
    closeModal();
    closeDrawer();
  };

  const deleteSchedule = (id: string) => {
    ingestionService.scheduler
      .delete(id)
      .then(() => setSchedules((prev) => prev.filter((s) => s.id !== id)))
      .catch((err: unknown) => {
        console.error('Delete schedule failed:', err);
        alert(err instanceof Error ? err.message : 'Failed to delete schedule');
      });
  };

  const clearLogs = () => {
    if (window.confirm('Clear all log entries?')) setLogs([]);
  };

  const eraseAllData = () => setDataRows([]);

  const removeLog = (id: string) => {
    setLogs((prev) => prev.filter((l) => l.id !== id));
  };

  const removeDataRow = (id: string) => {
    setDataRows((prev) => prev.filter((r) => r.id !== id));
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/65 backdrop-blur-md z-40 transition-opacity duration-200 ${
          overlayOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleOverlayClick}
        aria-hidden
      />

      <div className="w-full px-6 py-8 text-white min-h-screen">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-white">Scheduler</h2>
            <p className="text-sm text-gray-400 mt-0.5">
              Manage automated data sync jobs to your knowledge bases and stores.
            </p>
          </div>
          <button
            type="button"
            onClick={() => openScheduleModal()}
            className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-md py-2 px-4 text-sm cursor-pointer transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Schedule
          </button>
        </div>

        <div className="mb-6">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search schedules..."
            className="w-full max-w-[340px] px-4 py-2 text-sm bg-neutral-800 border border-neutral-600 rounded-md text-white placeholder:text-zinc-500 focus:border-indigo-600 outline-none transition-colors"
          />
        </div>

        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-3">
            <div className="text-xl font-semibold">{stats.total}</div>
            <div className="text-xs text-gray-500 mt-0.5 uppercase tracking-wider">Total</div>
          </div>
          <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-3">
            <div className="text-xl font-semibold text-green-400">{stats.active}</div>
            <div className="text-xs text-gray-500 mt-0.5 uppercase tracking-wider">Active</div>
          </div>
          <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-3">
            <div className="text-xl font-semibold text-yellow-400">{stats.running}</div>
            <div className="text-xs text-gray-500 mt-0.5 uppercase tracking-wider">Running</div>
          </div>
          <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-3">
            <div className="text-xl font-semibold text-red-400">{stats.errors}</div>
            <div className="text-xs text-gray-500 mt-0.5 uppercase tracking-wider">Errors</div>
          </div>
        </div>

        {schedulesError && (
          <p className="text-amber-500 text-sm mb-4">{schedulesError}</p>
        )}
        {schedulesLoading && (
          <p className="text-gray-500 text-sm mb-4">Loading schedules…</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {!schedulesLoading && filteredSchedules.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              {schedulesError ? 'Could not load schedules.' : 'No schedules yet. Click Add Schedule to create one.'}
            </div>
          )}
          {filteredSchedules.map((s) => (
            <div
              key={s.id}
              className="bg-neutral-900 border border-neutral-700 rounded-xl p-4 transition-colors hover:border-indigo-600 flex flex-col"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-2">
                  <div className="w-7 h-7 bg-neutral-800 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 text-sm">
                    {s.icon ?? '📄'}
                  </div>
                  <div>
                    <div className="text-white font-medium text-sm">{s.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {s.entity} → {s.destination}
                    </div>
                  </div>
                </div>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${statusBadgeClass[s.status]}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusDotClass[s.status]}`} />
                  {s.status.charAt(0).toUpperCase() + s.status.slice(1)}
                </span>
              </div>
              <div className="space-y-1.5 text-xs text-gray-300 mb-4">
                <div><span className="text-gray-500">Entity:</span> {s.entity}</div>
                <div><span className="text-gray-500">Destination:</span> {s.destination}</div>
                <div><span className="text-gray-500">Strategy:</span> {s.strategy}</div>
                <div><span className="text-gray-500">Schedule:</span> {s.schedule}</div>
                <div>
                  <span className="text-gray-500">Last Synced:</span>{' '}
                  {s.status === 'active' && s.lastSyncedMeta ? (
                    <span className="text-green-400">{s.lastSynced} · {s.lastSyncedMeta}</span>
                  ) : s.status === 'running' ? (
                    <span className="text-yellow-400">{s.lastSynced}</span>
                  ) : s.status === 'error' ? (
                    <span className="text-red-400">{s.lastSynced}</span>
                  ) : (
                    <span>{s.lastSynced}{s.lastSyncedMeta ? ` · ${s.lastSyncedMeta}` : ''}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-neutral-800">
                <Toggle
                  on={s.enabled}
                  onChange={() => setConfirmState({ type: 'toggle', schedule: s })}
                />
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => openDrawerLogs(s.name)}
                    className="p-0.5 text-indigo-400 hover:text-indigo-300 transition-colors inline-flex items-center"
                    title="Logs"
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => openCapturedDataModal(s.name)}
                    className="p-0.5 text-indigo-400 hover:text-indigo-300 transition-colors inline-flex items-center"
                    title="Data"
                  >
                    <Database className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    className="p-0.5 text-indigo-400 hover:text-indigo-300 transition-colors inline-flex items-center"
                    title="Run Now"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => openScheduleModal(s.id)}
                    className="p-0.5 text-yellow-400 hover:text-yellow-300 transition-colors inline-flex items-center"
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmState({ type: 'delete', schedule: s })}
                    className="p-0.5 text-red-400 hover:text-red-300 transition-colors inline-flex items-center"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <AddEditScheduleModal
        open={modalOpen}
        onClose={closeModal}
        editSchedule={
          editingScheduleId
            ? (() => {
                const s = schedules.find((x) => x.id === editingScheduleId);
                return s
                  ? {
                      id: s.id,
                      name: s.name,
                      entity: s.entityId ?? s.entity,
                      destination: s.destinationType ?? s.destination,
                      strategy: s.strategy,
                      schedule: s.schedule,
                      enabled: s.enabled,
                      strategyOptions: s.strategyOptions,
                      frequency: s.frequency,
                      runAt: s.runAt,
                      destinationId: s.destinationId,
                      entityName: s.entityName,
                      destinationName: s.destinationName,
                    }
                  : null;
              })()
            : null
        }
        onSubmit={handleScheduleSubmit}
      />

      <ConfirmPopup
        open={!!confirmState && confirmState.type === 'toggle'}
        onClose={() => setConfirmState(null)}
        title={confirmState?.schedule.enabled ? 'Disable schedule?' : 'Enable schedule?'}
        message={
          confirmState?.schedule.enabled
            ? `"${confirmState?.schedule.name}" will be disabled and will not run until turned back on.`
            : `"${confirmState?.schedule.name}" will be enabled and will run according to its schedule.`
        }
        confirmLabel={confirmState?.schedule.enabled ? 'Disable' : 'Enable'}
        variant="primary"
        onConfirm={() => {
          if (confirmState?.type === 'toggle' && confirmState.schedule) {
            toggleScheduleEnabled(confirmState.schedule.id);
          }
          setConfirmState(null);
        }}
      />
      <ConfirmPopup
        open={!!confirmState && confirmState.type === 'delete'}
        onClose={() => setConfirmState(null)}
        title="Delete schedule?"
        message={`"${confirmState?.schedule.name}" will be permanently removed. This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (confirmState?.type === 'delete' && confirmState.schedule) {
            deleteSchedule(confirmState.schedule.id);
          }
          setConfirmState(null);
        }}
      />

      {/* Centered Logs modal — 80% viewport, with search */}
      {drawerLogsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/65 backdrop-blur-md"
            onClick={closeDrawer}
            aria-hidden
          />
          <div
            className="relative w-[80vw] h-[80vh] flex flex-col rounded-2xl border border-neutral-700 bg-neutral-900 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-800 flex-shrink-0">
              <div>
                <div className="text-base font-semibold text-white">Logs</div>
                <div className="text-xs text-gray-500 mt-0.5">{drawerScheduleName}</div>
              </div>
              <button
                type="button"
                onClick={closeDrawer}
                className="p-1.5 text-gray-500 hover:text-white rounded-lg hover:bg-neutral-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-3 px-5 pt-4 pb-3 flex-shrink-0">
              <div className="bg-neutral-800 border border-neutral-700 rounded-lg p-3 text-center">
                <div className="text-base font-semibold text-green-400">1,247</div>
                <div className="text-xs text-gray-500 mt-0.5">Synced</div>
              </div>
              <div className="bg-neutral-800 border border-neutral-700 rounded-lg p-3 text-center">
                <div className="text-base font-semibold text-white">31s</div>
                <div className="text-xs text-gray-500 mt-0.5">Duration</div>
              </div>
              <div className="bg-neutral-800 border border-neutral-700 rounded-lg p-3 text-center">
                <div className="text-base font-semibold text-yellow-400">3</div>
                <div className="text-xs text-gray-500 mt-0.5">Skipped</div>
              </div>
              <div className="bg-neutral-800 border border-neutral-700 rounded-lg p-3 text-center">
                <div className="text-base font-semibold text-green-400">98.9%</div>
                <div className="text-xs text-gray-500 mt-0.5">Success</div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-neutral-800 flex-shrink-0">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  value={logsSearchQuery}
                  onChange={(e) => setLogsSearchQuery(e.target.value)}
                  placeholder="Search by time, level, or message..."
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-neutral-800 border border-neutral-600 text-white placeholder:text-gray-500 outline-none focus:border-indigo-500"
                />
              </div>
              <span className="text-xs text-gray-500">
                {filteredLogs.length} of {logs.length} entries
              </span>
              <button
                type="button"
                onClick={clearLogs}
                className="ml-auto py-1.5 px-3 text-xs rounded-lg border border-neutral-600 text-gray-400 hover:border-indigo-500 hover:text-white transition-colors"
              >
                Clear All
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5 space-y-0.5">
              {filteredLogs.map((log) => (
                <div
                  key={log.id}
                  className={`flex gap-3 py-2 px-2 rounded-md hover:bg-neutral-800 group cursor-default ${logLevelBorderClass[log.level]} pl-3`}
                >
                  <span className="text-xs text-gray-500 w-28 flex-shrink-0 pt-0.5 font-mono">{log.time}</span>
                  <div className="flex-1 flex flex-wrap gap-x-2 items-baseline">
                    <span className={`text-xs font-medium ${logLevelTextClass[log.level]}`}>{log.level}</span>
                    <span className="text-xs text-gray-300">{log.message}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLog(log.id)}
                    className="p-0.5 text-neutral-500 hover:text-red-400 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {filteredLogs.length === 0 && (
                <div className="py-8 text-center text-sm text-gray-500">
                  {logsSearchQuery.trim() ? 'No log entries match your search.' : 'No log entries.'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <CapturedDataModal
        open={drawerDataOpen}
        onClose={closeDrawer}
        scheduleName={drawerScheduleName}
        rows={dataRows}
        onEraseAll={eraseAllData}
        onExportCsv={() => {}}
        onDeleteRow={removeDataRow}
      />
    </>
  );
};

export default IngestionSchedules;
